import React, { useId, useRef, useState } from "react";
import styles from "./ShippingMethod.module.css";

export interface ShippingOption {
  id: string;
  name: string;
  description?: string;
  priceLabel: string;
}

export type DeliveryMode = "home" | "pickup";

export interface ShippingMethodProps {
  title?: string;
  options: ShippingOption[];
  selectedId?: string;
  onSelect: (id: string) => void;
  officesEnabled?: boolean;
  deliveryMode?: DeliveryMode;
  onDeliveryModeChange?: (mode: DeliveryMode) => void;
  pickupRateLabel?: string;
  showRadioButton?: boolean;
  borderColor?: string;
  backgroundColor?: string;
  activeBorderColor?: string;
}

export const ShippingMethod: React.FC<ShippingMethodProps> = ({
  title = "Método de envío",
  options,
  selectedId,
  onSelect,
  officesEnabled = false,
  deliveryMode = "home",
  onDeliveryModeChange,
  pickupRateLabel = "Recogida en oficina",
  showRadioButton = true,
  borderColor,
  backgroundColor,
  activeBorderColor,
}) => {
  const groupId = useId();
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const isPickupMode = officesEnabled && deliveryMode === "pickup";
  const [pickupNoticeDismissed, setPickupNoticeDismissed] = useState(false);

  const pickupOptionId = "__pickup_option__";
  const visibleOptions = isPickupMode
    ? [{ id: pickupOptionId, name: pickupRateLabel, priceLabel: "Gratis" }]
    : options;

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (visibleOptions.length === 0) return;
    let next: number | null = null;
    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      next = (index + 1) % visibleOptions.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      next = (index - 1 + visibleOptions.length) % visibleOptions.length;
    }
    if (next !== null) {
      e.preventDefault();
      if (!isPickupMode) {
        onSelect(visibleOptions[next].id);
      }
      optionRefs.current[next]?.focus();
    }
  };

  return (
    <div
      className={styles.wrapper}
      style={
        {
          "--shipping-border-color": borderColor,
          "--shipping-bg-color": backgroundColor,
          "--shipping-active-color": activeBorderColor,
        } as React.CSSProperties
      }
    >
      <div className={styles.container}>
        <p className={styles.title} id={`shipping-group-${groupId}`}>
          {title}
        </p>
        {isPickupMode && !pickupNoticeDismissed && (
          <div className={styles.pickupNotice}>
            <span>
              Puedes recoger tu pedido en oficinas hasta 3 días después de su
              entrega
            </span>
            <button
              type="button"
              className={styles.pickupNoticeDismiss}
              onClick={() => setPickupNoticeDismissed(true)}
              aria-label="Cerrar"
            >
              ✕
            </button>
          </div>
        )}
        {officesEnabled && (
          <div
            className={styles.deliveryTabs}
            role="tablist"
            aria-label="Modo de entrega"
          >
            <button
              type="button"
              role="tab"
              aria-selected={deliveryMode === "home"}
              className={
                deliveryMode === "home"
                  ? styles.deliveryTabActive
                  : styles.deliveryTab
              }
              onClick={() => {
                setPickupNoticeDismissed(false);
                onDeliveryModeChange?.("home");
              }}
            >
              🚚 Domicilio
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={deliveryMode === "pickup"}
              className={
                deliveryMode === "pickup"
                  ? styles.deliveryTabActive
                  : styles.deliveryTab
              }
              onClick={() => onDeliveryModeChange?.("pickup")}
            >
              🏪 Recoger en oficina
            </button>
          </div>
        )}
        <div
          className={styles.optionsList}
          role="radiogroup"
          aria-labelledby={`shipping-group-${groupId}`}
          aria-required="true"
        >
          {visibleOptions.map((option, index) => {
            const isSelected = isPickupMode
              ? option.id === pickupOptionId
              : option.id === selectedId;
            return (
              <button
                key={option.id}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={isSelected}
                tabIndex={isSelected ? 0 : -1}
                className={isSelected ? styles.optionSelected : styles.option}
                onClick={() => {
                  if (!isPickupMode) {
                    onSelect(option.id);
                  }
                }}
                onKeyDown={(e) => handleKeyDown(e, index)}
              >
                {showRadioButton && (
                  <div
                    className={isSelected ? styles.radioSelected : styles.radio}
                  >
                    {isSelected && <span className={styles.radioInner} />}
                  </div>
                )}
                <div className={styles.optionContent}>
                  <div className={styles.optionDetails}>
                    <span className={styles.optionName}>{option.name}</span>
                    {option.description && (
                      <span className={styles.optionDescription}>
                        {option.description}
                      </span>
                    )}
                  </div>
                  <div className={styles.optionPricing}>
                    <span className={styles.optionPrice}>
                      {option.priceLabel}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
